import React, { useState } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Paper,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Edit as EditIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  LinkedIn as LinkedInIcon,
  Email as EmailIcon,
  Work as WorkIcon,
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  InfoOutlined as InfoOutlinedIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
} from '@mui/icons-material';
import { zoullooColors } from '../../theme';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useClientByUserId } from '../../hooks/useClient';
import {
  useAdminClientTalentMatchingsForClient,
  useExcludeClientTalentMatching,
} from '../../hooks/useAnonymizedTalents';
import { ClientMatchedTalentAdminRow } from '../../models/ClientTalentMatchingAdmin';
import { AssignTalentDialog } from '../../components/client/AssignTalentDialog';

const ClientDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const userId = parseInt(id || '0');

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { data: client, isLoading, error } = useClientByUserId(userId);

  // Daily-matcher output, scoped to this client only — rendered inline so the
  // CSM can verify the worker fired correctly without leaving the file.
  const {
    data: matchingRows = [],
    isLoading: isLoadingMatchings,
    isError: isErrorMatchings,
  } = useAdminClientTalentMatchingsForClient(userId, !!userId);

  const clientMatchingRow = matchingRows[0] ?? null;
  const lastRefreshedAt = clientMatchingRow?.lastRefreshedAt
    ? new Date(clientMatchingRow.lastRefreshedAt).toLocaleString(i18n.language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  // CSM action: exclude a talent from this client's live matched batch.
  const excludeMutation = useExcludeClientTalentMatching();
  const [excludeTarget, setExcludeTarget] =
    useState<ClientMatchedTalentAdminRow | null>(null);

  // CSM action: manually assign a talent (search-and-pick dialog).
  const [assignOpen, setAssignOpen] = useState(false);

  const handleExcludeConfirmed = async () => {
    if (!excludeTarget) {
      return;
    }
    try {
      await excludeMutation.mutateAsync({
        matchingId: excludeTarget.matchingId,
        clientUserId: userId,
      });
    } finally {
      setExcludeTarget(null);
    }
  };

  const handleEdit = () => {
    navigate(`/clients/${userId}/edit`);
  };
  
  const handleNavigateToList = () => {
    navigate('/clients');
  };
  
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  if (error || !client) {
    return (
      <Box p={3}>
        <Typography color="error">
          {t('client.errorLoadingClient')}: {error?.message || t('client.notFound')}
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: zoullooColors.primary.main, width: 56, height: 56 }}>
            {client.user?.firstName?.[0]}{client.user?.lastName?.[0]}
          </Avatar>
          <Typography variant="h2">
            {client.user?.firstName} {client.user?.lastName}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={handleEdit}
          sx={{
            backgroundColor: zoullooColors.secondary.main,
            '&:hover': {
              backgroundColor: zoullooColors.secondary.dark,
            },
          }}
        >
          {t('common.edit')}
        </Button>
      </Box>
      
      <Breadcrumbs separator="›" aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link
          underline="hover"
          color="inherit"
          onClick={handleNavigateToList}
          sx={{ cursor: 'pointer' }}
        >
          {t('client.clientList')}
        </Link>
        <Typography color="text.primary">
          {client.user?.firstName} {client.user?.lastName}
        </Typography>
      </Breadcrumbs>
      
      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Box display="flex" alignItems="center" mb={2}>
              <Typography variant="h4" color={zoullooColors.secondary.dark}>
                {t('client.basicInformation')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid size={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon sx={{ color: zoullooColors.secondary.main }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('client.email')}
                  </Typography>
                </Box>
                <Typography variant="body1" gutterBottom>
                  {client.user?.email}
                </Typography>
              </Grid>
              
              <Grid size={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <WorkIcon sx={{ color: zoullooColors.secondary.main }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('client.jobTitle')}
                  </Typography>
                </Box>
                <Typography variant="body1" gutterBottom>
                  {client.jobTitle || '-'}
                </Typography>
              </Grid>
              
              <Grid size={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <BusinessIcon sx={{ color: zoullooColors.secondary.main }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('client.company')}
                  </Typography>
                </Box>
                <Typography variant="body1" gutterBottom>
                  {client.company?.legalName || '-'}
                </Typography>
              </Grid>
              
              {client.phoneNumber && (
                <Grid size={12}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PhoneIcon sx={{ color: zoullooColors.secondary.main }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('client.phone')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" gutterBottom>
                    {client.phoneNumber}
                  </Typography>
                </Grid>
              )}
              
              {client.linkedInUrl && (
                <Grid size={12}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <LinkedInIcon sx={{ color: zoullooColors.secondary.main }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('client.linkedIn')}
                    </Typography>
                  </Box>
                  <Link href={client.linkedInUrl} target="_blank" rel="noopener">
                    {client.linkedInUrl}
                  </Link>
                </Grid>
              )}
              
              <Grid size={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('client.onboardingStatus')}
                </Typography>
                <Chip
                  label={client.onboardingCompleted ? t('client.onboardingCompleted') : t('client.onboardingPending')}
                  color={client.onboardingCompleted ? 'success' : 'warning'}
                  sx={{ mt: 1 }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Preferences and Expertise */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Box display="flex" alignItems="center" mb={2}>
              <Typography variant="h4" color={zoullooColors.secondary.dark}>
                {t('client.preferencesAndExpertise')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('client.freelancerFrequency')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {client.freelancerFrequency 
                    ? t(`enums.freelancerFrequency.${client.freelancerFrequency}`)
                    : '-'}
                </Typography>
              </Grid>
              
              <Grid size={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('client.communicationChannel')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {client.preferredCommunicationChannels && client.preferredCommunicationChannels.length > 0
                    ? client.preferredCommunicationChannels
                        .map(channel => t(`enums.communicationChannel.${channel}`))
                        .join(', ')
                    : '-'}
                </Typography>
              </Grid>
              
              <Grid size={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('client.usesPlatforms')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {client.usesPlatforms ? t('common.yes') : t('common.no')}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Expertise Information */}
        <Grid size={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h4" color={zoullooColors.secondary.dark} mb={2}>
              {t('client.expertiseInformation')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={3}>
              {/* Freelancer Expertises */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('client.freelancerExpertises')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {client.freelancerExpertises?.length > 0 ? (
                    client.freelancerExpertises.map((expertise) => (
                      <Chip
                        key={expertise.id}
                        label={t(`Expertises.${expertise.name}`)}
                        color="primary"
                        variant="outlined"
                      />
                    ))
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      {t('common.none')}
                    </Typography>
                  )}
                </Box>
              </Grid>
              
              {/* Business Domains */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('client.competenceDomains')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {client.businessDomains?.length > 0 ? (
                    client.businessDomains.map((domain) => (
                      <Chip
                        key={domain.id}
                        label={t(`BusinessDomains.${domain.label}`)}
                        color="secondary"
                        variant="outlined"
                      />
                    ))
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      {t('common.none')}
                    </Typography>
                  )}
                </Box>
              </Grid>
              
              {/* Tools */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('client.tools')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {client.tools?.length > 0 ? (
                    client.tools.map((tool) => (
                      <Chip
                        key={tool.id}
                        label={tool.label}
                        variant="outlined"
                      />
                    ))
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      {t('common.none')}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Matched Talents — daily worker audit, inline so CSMs see the
            matcher's output without leaving the client file. Scoped to this
            client only (one network call, no full-list scan). */}
        <Grid size={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'center' },
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                mb: 2,
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <AutoAwesomeIcon sx={{ color: zoullooColors.secondary.main }} />
                <Typography variant="h4" color={zoullooColors.secondary.dark}>
                  {t('anonymizedTalent.adminAuditTitle')}
                </Typography>
                <Tooltip
                  title={t('anonymizedTalent.adminAuditScoreHelp')}
                  arrow
                >
                  <InfoOutlinedIcon
                    fontSize="small"
                    sx={{ color: zoullooColors.text.secondary, cursor: 'help' }}
                  />
                </Tooltip>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                {lastRefreshedAt && (
                  <Typography variant="caption" color="text.secondary">
                    {t('anonymizedTalent.adminAuditLastRefreshed', { date: lastRefreshedAt })}
                  </Typography>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setAssignOpen(true)}
                >
                  {t('anonymizedTalent.adminAuditAssignButton')}
                </Button>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {isLoadingMatchings && (
              <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {isErrorMatchings && !isLoadingMatchings && (
              <Box
                sx={{
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: zoullooColors.error.alpha.lighter,
                  border: `1px solid ${zoullooColors.error.light}`,
                  borderRadius: 2,
                  color: zoullooColors.error.dark,
                }}
              >
                {t('anonymizedTalent.adminAuditLoadError')}
              </Box>
            )}

            {!isLoadingMatchings && !isErrorMatchings && !clientMatchingRow && (
              <Box
                sx={{
                  p: 4,
                  textAlign: 'center',
                  backgroundColor: zoullooColors.proposal.magnolia,
                  borderRadius: 2,
                  border: `1px dashed ${zoullooColors.secondary.light}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: zoullooColors.secondary.dark,
                    mb: 0.5,
                  }}
                >
                  {t('anonymizedTalent.adminAuditEmptyTitle')}
                </Typography>
                <Typography sx={{ fontSize: 13, color: zoullooColors.text.secondary }}>
                  {t('anonymizedTalent.adminAuditEmptySubtitle')}
                </Typography>
              </Box>
            )}

            {!isLoadingMatchings && !isErrorMatchings && clientMatchingRow && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Chip
                    size="small"
                    label={t('anonymizedTalent.adminAuditMatchings', {
                      count: clientMatchingRow.matchingsCount,
                    })}
                    sx={{
                      backgroundColor: zoullooColors.proposal.magnolia,
                      color: zoullooColors.secondary.dark,
                      fontWeight: 700,
                    }}
                  />
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography variant="caption" fontWeight={700}>
                            {t('anonymizedTalent.adminAuditTalent')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" fontWeight={700}>
                            {t('anonymizedTalent.adminAuditExpertise')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" fontWeight={700}>
                            {t('anonymizedTalent.adminAuditProfileTitle')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="caption" fontWeight={700}>
                            {t('anonymizedTalent.adminAuditScore')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientMatchingRow.matchings.map((m) => (
                        <Tooltip
                          key={m.matchingId}
                          title={t('anonymizedTalent.previewRowTooltip')}
                          arrow
                          placement="left"
                        >
                          <TableRow
                            hover
                            onClick={() =>
                              navigate(
                                `/talent-anonymized-preview/${m.freelancerProfileId}?clientId=${userId}`
                              )
                            }
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>{m.freelancerFullName || '—'}</TableCell>
                            <TableCell>
                              {m.freelancerExpertise
                                ? t(`Expertises.${m.freelancerExpertise}`, m.freelancerExpertise)
                                : '—'}
                            </TableCell>
                            <TableCell>{m.freelancerProfileTitle ?? '—'}</TableCell>
                            <TableCell align="center">
                              <Chip
                                size="small"
                                label={m.matchScore}
                                sx={{
                                  backgroundColor: zoullooColors.proposal.seashell,
                                  color: zoullooColors.primary.dark,
                                  fontWeight: 700,
                                  minWidth: 36,
                                }}
                              />
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={(e) => e.stopPropagation()}
                              sx={{ cursor: 'default' }}
                            >
                              <Tooltip
                                title={t('anonymizedTalent.adminAuditExcludeTooltip')}
                                arrow
                              >
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={excludeMutation.isPending}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExcludeTarget(m);
                                  }}
                                >
                                  <RemoveCircleOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        </Tooltip>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={excludeTarget !== null}
        onClose={() => setExcludeTarget(null)}
        aria-labelledby="exclude-matching-dialog-title"
      >
        <DialogTitle id="exclude-matching-dialog-title">
          {t('anonymizedTalent.adminAuditExcludeTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('anonymizedTalent.adminAuditExcludeMessage', {
              name:
                excludeTarget?.freelancerFullName ||
                t('anonymizedTalent.defaultTitle'),
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setExcludeTarget(null)}
            disabled={excludeMutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleExcludeConfirmed}
            disabled={excludeMutation.isPending}
          >
            {t('anonymizedTalent.adminAuditExcludeConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <AssignTalentDialog
        clientUserId={userId}
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
      />
    </Box>
  );
};

export default ClientDetails;